package me.tbsten.compose.preview.lab.field.component

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.unit.dp
import me.tbsten.compose.preview.lab.MutablePreviewLabField
import me.tbsten.compose.preview.lab.ui.PreviewLabTheme
import me.tbsten.compose.preview.lab.ui.components.PreviewLabText
import me.tbsten.compose.preview.lab.ui.components.textfield.PreviewLabOutlinedTextField

/**
 * Helper for UI of Fields that can be input with TextField.
 *
 * @param toString Converts the state value to a string.
 * @param toValue Converts the string to a state value.
 * @param prefix Composable to be displayed as a prefix; if null, it is not displayed.
 * @param suffix Composable to be displayed as a suffix; if null, it is not displayed.
 * @param placeholder Composable to be displayed as a placeholder; if null, the label is displayed with [Text].
 */
@Composable
fun <Value> MutablePreviewLabField<Value>.TextFieldContent(
    toString: (Value) -> String,
    toValue: (String) -> Result<Value>,
    modifier: Modifier = Modifier,
    prefix: (@Composable () -> Unit)? = null,
    suffix: (@Composable () -> Unit)? = null,
    placeholder: (@Composable () -> Unit)? = null,
) = TextFieldContent(
    label = label,
    value = value,
    onValueChange = { value = it },
    toString = toString,
    toValue = toValue,
    modifier = modifier,
    prefix = prefix,
    suffix = suffix,
    placeholder = placeholder,
)

@Composable
fun <Value> TextFieldContent(
    label: String,
    value: Value,
    onValueChange: (Value) -> Unit,
    toString: (Value) -> String,
    toValue: (String) -> Result<Value>,
    modifier: Modifier = Modifier,
    prefix: (@Composable () -> Unit)? = null,
    suffix: (@Composable () -> Unit)? = null,
    placeholder: (@Composable () -> Unit)? = null,
    textStyle: TextStyle = PreviewLabTheme.typography.input,
) {
    var textFieldText by remember(value.toString()) { mutableStateOf(toString(value)) }
    var isValid by remember {
        mutableStateOf(true)
    }

    Row(
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        modifier = modifier,
    ) {
        PreviewLabOutlinedTextField(
            value = textFieldText,
            onValueChange = {
                textFieldText = it
                toValue(it)
                    .also { isValid = it.isSuccess }
                    .onSuccess { onValueChange(it) }
            },
            placeholder = {
                placeholder?.invoke() ?: PreviewLabText(label)
            },
            prefix = prefix,
            suffix = suffix,
            isError = !isValid,
            textStyle = textStyle,
        )
    }
}
